import { useGlobalStore } from '@/stores/global.ts';
import { constants } from '@/constants/constants.ts';
import { computed, type ComputedRef, type Ref, type ShallowRef } from 'vue';
import { PlaceholderType } from '@/types/enums/PlaceholderType.ts';
import type {
  Placeholder,
  WebPageDetails
} from '@/composables/placement/placementTypes.ts';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { DragResizeContext } from '@deepcloud/draggable';
import {
  getPageInfo,
  getTopOffset,
  pixelsToPoints,
  scrollToPlaceholder,
  setDefaultVisiblePlaceholderState,
  setPlaceHolderDimensions,
  setPlaceHolderResizeThresholds
} from '@/composables/placement/usePlacementUtility.ts';

export const useSealAnnotation = (
  currentPlaceHolders:
    | MaybeRefOrGetter
    | Ref<Placeholder[] | Ref[] | undefined>,
  placeholderTemplateRefs: MaybeRefOrGetter | ShallowRef | undefined
) => {
  const globalStore = useGlobalStore();

  const sealAnnotationPlaceholder: ComputedRef<Placeholder> = computed(() => {
    return (
      currentPlaceHolders?.value?.find(
        (placeholder: Placeholder) =>
          placeholder.placeHolderType === PlaceholderType.Annotation
      ) || ({ text: '' } as Partial<Placeholder>)
    );
  });

  const sealPlaceholder: ComputedRef<Placeholder> = computed(() => {
    return currentPlaceHolders?.value?.find(
      (placeholder: Placeholder) =>
        placeholder.placeHolderType === PlaceholderType.SEAL
    );
  });

  const sealRefContext: ComputedRef<DragResizeContext> = computed(() =>
    sealPlaceholder.value
      ? placeholderTemplateRefs?.value[sealPlaceholder.value.index]?.context
      : null
  );

  async function handleInitialSealAnnotationPosition() {
    if (
      !sealPlaceholder.value.position ||
      !sealAnnotationPlaceholder.value ||
      !sealAnnotationPlaceholder.value?.position
    )
      return;
    const currentPageNumber = sealPlaceholder?.value?.isVisible
      ? sealPlaceholder.value?.position?.pageNumber
      : globalStore.currentPage;

    const { webPageDetails, currentPageNode, pdfJsPageDetails } =
      await getPageInfo(currentPageNumber);

    // Use the same width as the seal placeholder if the placeholder is visible
    sealAnnotationPlaceholder.value.position.width = sealPlaceholder.value
      .isVisible
      ? sealRefContext.value.width
      : pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.M.WIDTH) *
        pdfJsPageDetails.viewport.scale;
    sealAnnotationPlaceholder.value.position.height =
      pixelsToPoints(constants.SEAL_ANNOTATION_SIZE.M.HEIGHT) *
      pdfJsPageDetails.viewport.scale;

    sealAnnotationPlaceholder.value.resize.minWidth = pixelsToPoints(
      constants.SIGNATURE_SIZE.XS.WIDTH
    );
    sealAnnotationPlaceholder.value.resize.minHeight = pixelsToPoints(
      constants.SIGNATURE_SIZE.XS.HEIGHT
    );
    sealAnnotationPlaceholder.value.resize.maxWidth = webPageDetails.width - 1;
    sealAnnotationPlaceholder.value.resize.maxHeight = pixelsToPoints(
      constants.SEAL_ANNOTATION_SIZE.L.HEIGHT
    );

    const sealYPosRelativeToPage = Math.abs(
      parseFloat(
        (sealRefContext.value.draggingElement as HTMLElement).style.top
      ) - getTopOffset(currentPageNode)
    );

    const sealPlaceholderHeight = sealRefContext.value.height;
    const calculatedY =
      webPageDetails.height - sealYPosRelativeToPage - sealPlaceholderHeight;

    const hasAnnotationEnoughSpace = sealPlaceholder?.value?.isVisible
      ? Boolean(sealAnnotationPlaceholder.value.position.height <= calculatedY)
      : false;

    if (sealPlaceholder.value.isVisible && hasAnnotationEnoughSpace) {
      sealAnnotationPlaceholder.value.position.x = sealRefContext.value.x;
      sealAnnotationPlaceholder.value.position.y =
        sealRefContext.value.y + sealRefContext.value.height;
    } else {
      setDefaultPlaceholderPosition(
        sealAnnotationPlaceholder.value,
        webPageDetails,
        currentPageNode
      );
    }

    setDefaultVisiblePlaceholderState(
      sealAnnotationPlaceholder.value,
      currentPageNumber
    );

    scrollToPlaceholder(sealAnnotationPlaceholder.value);
  }

  async function handleInitialSealPosition() {
    if (!sealPlaceholder.value || !sealPlaceholder.value.position) return;
    const globalStore = useGlobalStore();
    const currentPageNumber = globalStore.currentPage;

    const { webPageDetails, currentPageNode, pdfJsPageDetails } =
      await getPageInfo(currentPageNumber);

    setPlaceHolderDimensions(
      sealPlaceholder.value,
      pdfJsPageDetails.viewport.scale
    );
    setPlaceHolderResizeThresholds(
      sealPlaceholder.value,
      pdfJsPageDetails.viewport.scale
    );
    setDefaultPlaceholderPosition(
      sealPlaceholder.value,
      webPageDetails,
      currentPageNode
    );
    setDefaultVisiblePlaceholderState(sealPlaceholder.value, currentPageNumber);
    scrollToPlaceholder(sealPlaceholder.value);
  }

  function setDefaultPlaceholderPosition(
    placeholder: Placeholder,
    webPageDetails: WebPageDetails,
    currentPageNode: HTMLElement
  ) {
    const DEFAULT_OFFSET_FACTOR = 0.05;
    const DEFAULT_OFFSET_Y_FACTOR = 0.2;
    if (!placeholder || !placeholder.position) return;

    placeholder.position.x =
      currentPageNode.offsetLeft +
      webPageDetails.pageBoundingRect.width * DEFAULT_OFFSET_FACTOR;

    const yFactor =
      placeholder.placeHolderType === PlaceholderType.Annotation
        ? DEFAULT_OFFSET_FACTOR
        : DEFAULT_OFFSET_Y_FACTOR;

    placeholder.position.y =
      webPageDetails.pageBoundingRect.height * yFactor +
      getTopOffset(currentPageNode as HTMLElement);
  }

  return {
    sealAnnotationPlaceholder,
    sealPlaceholder,
    handleInitialSealAnnotationPosition,
    handleInitialSealPosition
  };
};
